import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  fetchExistingRating,
  fetchPublicOrderInfo,
  resolveImageUrl,
  submitRating,
} from "../services/api";

// Translation table for the rating screen. Customer's language sits in
// localStorage from the menu-page toggle.
const T = {
  en: {
    loading: "Loading…",
    thank_you: "🙏 Thank you",
    order_hash: "Order",
    feedback_received: "Your feedback was received. Hotels use this to recognize the staff who took good care of you.",
    food: "Food",
    service: "Service",
    back_to_start: "Back to start",
    how_was: "How was your meal?",
    food_comment: "Anything to share about the food? (optional)",
    service_comment: "Anything about your waiter's service? (optional)",
    skip: "Skip",
    submit_rating: "Submit rating",
    sending: "Sending…",
    rate_dishes: "Rate the dishes (optional)",
    dishes_hint: "Tap stars for any dish you\u2019d like to rate.",
  },
  nl: {
    loading: "Laden…",
    thank_you: "🙏 Bedankt",
    order_hash: "Bestelling",
    feedback_received: "We hebben je beoordeling ontvangen. Het restaurant gebruikt dit om hun beste medewerkers te bedanken.",
    food: "Eten",
    service: "Service",
    back_to_start: "Terug naar start",
    how_was: "Hoe was je maaltijd?",
    food_comment: "Wil je iets over het eten kwijt? (optioneel)",
    service_comment: "En over de bediening? (optioneel)",
    skip: "Overslaan",
    submit_rating: "Beoordeling versturen",
    sending: "Versturen…",
    rate_dishes: "Beoordeel de gerechten (optioneel)",
    dishes_hint: "Tik sterren aan voor elk gerecht dat je wilt beoordelen.",
  },
  am: {
    loading: "በመጫን ላይ…",
    thank_you: "🙏 አመሰግናለሁ",
    order_hash: "ትዕዛዝ",
    feedback_received: "ግምገማዎ ደርሷል። ሆቴሎች ይህን አስተውለው ሰራተኞቻቸውን ያደንቃሉ።",
    food: "ምግብ",
    service: "አገልግሎት",
    back_to_start: "ወደ መነሻ",
    how_was: "ምግቡ እንዴት ነበር?",
    food_comment: "ስለ ምግቡ የሚሉት ካለ ይጻፉ (አማራጭ)",
    service_comment: "ስለ አስተናጋጁ የሚሉት ካለ (አማራጭ)",
    skip: "ዝለል",
    submit_rating: "ግምገማ ላክ",
    sending: "በመላክ ላይ…",
    rate_dishes: "ምግቦቹን ይገምግሙ (አማራጭ)",
    dishes_hint: "መገምገም ለሚፈልጉት ምግብ ኮከቦቹን ይንኩ።",
  },
  om: {
    loading: "Fe'aa…",
    thank_you: "🙏 Galatoomaa",
    order_hash: "Ajaja",
    feedback_received: "Yaadni keessan nu gaheera. Manneen keessummeessitootaa hojjettoota gaarii ittiin kabajaniif kana fayyadamu.",
    food: "Nyaata",
    service: "Tajaajila",
    back_to_start: "Gara jalqabaa",
    how_was: "Nyaanni kee akkam ture?",
    food_comment: "Waa'ee nyaataa wanti jechuu barbaaddu (filannoo)",
    service_comment: "Waa'ee tajaajila waaroo (filannoo)",
    skip: "Darbi",
    submit_rating: "Madaallii ergi",
    sending: "Ergamaa…",
    rate_dishes: "Nyaata madaali (filannoo)",
    dishes_hint: "Nyaata madaaluu barbaaddu kamiyyuu urjii tuqi.",
  },
};
function pickT(lang) {
  const dict = T[lang] || T.en;
  return (k) => dict[k] || T.en[k] || k;
}

// Compact brand strip shown above every rating-page state. Renders the
// hotel/restaurant's logo if they're on Business tier and have one
// uploaded, falling back to the generic ODRA mark.
function BrandStrip({ orderInfo }) {
  const logo = orderInfo?.logo_url ? resolveImageUrl(orderInfo.logo_url) : null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 8,
      }}
    >
      <img
        src={logo || "/logo64.png"}
        alt=""
        width="40"
        height="40"
        style={{ borderRadius: 8, objectFit: "contain", background: "#fff" }}
      />
      <div style={{ flex: 1, fontWeight: 600 }}>
        {orderInfo?.restaurant_name || "Your meal"}
      </div>
    </div>
  );
}

/**
 * Customer rating screen.
 *
 * Flow:
 *   1. Load any existing rating for this order. If one exists, show a
 *      "thanks, you already rated" view (so a guest can refresh without
 *      re-submitting).
 *   2. Otherwise show two five-star inputs (food + service) and two
 *      optional comment textareas.
 *   3. Submit POST /ratings/{orderId}. Possible backend responses:
 *        201 -> thank-you state
 *        400 -> "your order hasn't been served yet" (we surface verbatim)
 *        409 -> "already rated" (race with another tab)
 *
 * Public-by-order-id, mirroring the receipt page. We don't need auth
 * because the order id is already in the URL and a hostile guest can't
 * do much with rating a meal they ate.
 */

function StarPicker({ value, onChange, label, idPrefix }) {
  return (
    <fieldset
      style={{
        border: "1px solid var(--border, #e2ddd5)",
        borderRadius: 12,
        padding: 12,
        margin: "12px 0",
      }}
    >
      <legend style={{ padding: "0 8px", fontWeight: 600 }}>{label}</legend>
      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            aria-label={`${n} star${n === 1 ? "" : "s"}`}
            style={{
              fontSize: 36,
              lineHeight: 1,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: n <= value ? "#d4af37" : "#cbd2d9",
              padding: "4px 8px",
            }}
            data-testid={`${idPrefix}-star-${n}`}
          >
            ★
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function MiniStars({ value, onChange, ariaLabel }) {
  return (
    <div role="group" aria-label={ariaLabel} style={{ display: "flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n === value ? 0 : n)}
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
          style={{
            fontSize: 22,
            lineHeight: 1,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: n <= value ? "#d4af37" : "#cbd2d9",
            padding: "2px 3px",
          }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

export default function RatingPage() {
  // The :orderId route param now carries the order's unguessable public_token.
  const { orderId: token } = useParams();
  const [existing, setExisting] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [food, setFood] = useState(0);
  const [service, setService] = useState(0);
  const [foodComment, setFoodComment] = useState("");
  const [serviceComment, setServiceComment] = useState("");
  const [itemRatings, setItemRatings] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      // Both calls are independent; race them and ignore failures
      // individually so a hiccup on one doesn't blank the page.
      const [ratingRes, infoRes] = await Promise.allSettled([
        fetchExistingRating(token),
        fetchPublicOrderInfo(token),
      ]);
      if (ratingRes.status === "fulfilled" && ratingRes.value?.id) {
        setExisting(ratingRes.value);
      }
      if (infoRes.status === "fulfilled" && infoRes.value) {
        setOrderInfo(infoRes.value);
      }
      setLoading(false);
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!food || !service) {
      setError("Please tap a star for both food and service.");
      return;
    }
    setSubmitting(true);
    try {
      const item_ratings = Object.entries(itemRatings)
        .filter(([, v]) => v > 0)
        .map(([id, v]) => ({ order_item_id: Number(id), food_rating: v }));
      await submitRating(token, {
        food_rating: food,
        service_rating: service,
        food_comment: foodComment.trim() || undefined,
        service_comment: serviceComment.trim() || undefined,
        item_ratings: item_ratings.length ? item_ratings : undefined,
      });
      setSubmitted(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 400) {
        // E.g. "This order is still 'preparing'. You can rate it once it has been served."
        setError(typeof detail === "string" ? detail : "Your order isn't ready to rate yet.");
      } else if (status === 409) {
        setError("Looks like this order was already rated. Thank you!");
      } else {
        setError(typeof detail === "string" ? detail : "Could not submit your rating. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const lang = localStorage.getItem("menulink_lang") || "en";
  const t = pickT(lang);
  // Short human order number for the "Order #…" line (the URL carries the long
  // public_token). Comes from the public order-info payload.
  const displayId = orderInfo?.order_id ?? null;

  if (loading) return <div className="page center">{t("loading")}</div>;

  if (existing || submitted) {
    return (
      <div className="page">
        <BrandStrip orderInfo={orderInfo} />
        <div className="brand-bar">
          <h1>{t("thank_you")}</h1>
          {displayId != null && (
            <div className="sub">{t("order_hash")} #{displayId}</div>
          )}
        </div>
        <div className="success" style={{ marginTop: 16 }}>
          {t("feedback_received")}
        </div>
        {existing && (
          <div className="card" style={{ marginTop: 16 }}>
            <p style={{ marginTop: 0 }}>
              <b>{t("food")}:</b> {existing.food_rating}/5 &nbsp;·&nbsp;
              <b>{t("service")}:</b> {existing.service_rating}/5
            </p>
            {existing.food_comment && (
              <p className="muted" style={{ marginBottom: 4 }}>
                <i>{t("food")}:</i> {existing.food_comment}
              </p>
            )}
            {existing.service_comment && (
              <p className="muted" style={{ marginTop: 0 }}>
                <i>{t("service")}:</i> {existing.service_comment}
              </p>
            )}
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <Link to="/" className="btn ghost">
            {t("back_to_start")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <BrandStrip orderInfo={orderInfo} />
      <div className="brand-bar">
        <h1>{t("how_was")}</h1>
        {displayId != null && (
          <div className="sub">{t("order_hash")} #{displayId}</div>
        )}
      </div>

      <form className="card" onSubmit={submit} style={{ marginTop: 16 }}>
        <StarPicker
          label={t("food")}
          value={food}
          onChange={setFood}
          idPrefix="food"
        />
        <textarea
          className="notes"
          placeholder={t("food_comment")}
          value={foodComment}
          onChange={(e) => setFoodComment(e.target.value)}
        />

        <StarPicker
          label={t("service")}
          value={service}
          onChange={setService}
          idPrefix="service"
        />
        <textarea
          className="notes"
          placeholder={t("service_comment")}
          value={serviceComment}
          onChange={(e) => setServiceComment(e.target.value)}
        />

        {orderInfo?.items?.length ? (
          <fieldset
            style={{
              border: "1px solid var(--border, #e2ddd5)",
              borderRadius: 12,
              padding: 12,
              margin: "12px 0",
            }}
          >
            <legend style={{ padding: "0 8px", fontWeight: 600 }}>{t("rate_dishes")}</legend>
            <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>{t("dishes_hint")}</div>
            {orderInfo.items.map((it) => (
              <div
                key={it.id}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "4px 0" }}
              >
                <span style={{ fontSize: 14 }}>
                  {it.quantity > 1 ? `${it.quantity}\u00d7 ` : ""}{it.name || `#${it.menu_id}`}
                </span>
                <MiniStars
                  ariaLabel={it.name || `item ${it.id}`}
                  value={itemRatings[it.id] || 0}
                  onChange={(v) => setItemRatings((cur) => ({ ...cur, [it.id]: v }))}
                />
              </div>
            ))}
          </fieldset>
        ) : null}

        {error && <div className="notice" style={{ marginTop: 12 }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <Link to="/" className="btn secondary">
            {t("skip")}
          </Link>
          <button className="btn" type="submit" disabled={submitting}>
            {submitting ? t("sending") : t("submit_rating")}
          </button>
        </div>
      </form>
    </div>
  );
}
